import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { Add, Close, Search, West as WestIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { WorkflowModalLayout } from '@/components/shared/workflows/WorkflowModalLayout';
import { WorkflowCartRow } from '@/components/shared/workflows/WorkflowCartRow';
import type { MaintenanceRequestReceivableItemViewRow } from '@/services/maintenance';
import { useAuth } from '@/hooks/useAuth';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { groupMaintenanceCartItems } from '@/components/maintenance/groupMaintenanceCartItems';
import { groupMaintenanceReceivableItems } from '@/components/maintenance/groupMaintenanceReceivableItems';
import {
  useReceiveFromMaintenance,
  useMaintenanceCart,
  useAddToMaintenanceCart,
  useRemoveFromMaintenanceCart,
  useClearMaintenanceCart,
} from '@/hooks/mutations/maintenance';
import { useMaintenanceRequestReceivableItems } from '@/hooks/queries/maintenance';
import { useQueryClient } from '@tanstack/react-query';
import { cartQueryKeys } from '@/hooks/queries/cart/useCartQueries';
import { cartService } from '@/services/cart';
import { multiWordMatch } from '@/utils/multiWordMatch';

export interface ReceiveFromMaintenanceModalProps {
  open: boolean;
  maintenanceRequestId: string | null;
  documentNumber?: string | null;
  onClose: () => void;
}

const clampQuantity = (value: number, max: number): number => {
  if (!Number.isFinite(value)) {
    return 1;
  }
  return Math.min(Math.max(Math.floor(value), 1), Math.max(max, 1));
};

export const ReceiveFromMaintenanceModal: React.FC<ReceiveFromMaintenanceModalProps> = ({
  open,
  maintenanceRequestId,
  documentNumber,
  onClose,
}) => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const workflowType = 'maintenance-receive' as const;

  const { data: cartData } = useMaintenanceCart(userId, workflowType, {
    enabled: open && !!userId,
  });
  const { mutateAsync: addToCart, isPending: isAdding } = useAddToMaintenanceCart(workflowType);
  const { mutateAsync: removeFromCart, isPending: isRemoving } =
    useRemoveFromMaintenanceCart(workflowType);
  const { mutateAsync: clearCart } = useClearMaintenanceCart(workflowType);

  const {
    data: receivableItems = [],
    isLoading,
    isError,
    refetch,
  } = useMaintenanceRequestReceivableItems(maintenanceRequestId, {
    enabled: open && !!maintenanceRequestId,
  });
  const { mutateAsync: receiveFromMaintenance, isPending: isReceiving } =
    useReceiveFromMaintenance();

  const queryClient = useQueryClient();

  const [searchText, setSearchText] = useState('');
  const [qtyDrafts, setQtyDrafts] = useState<Record<string, number>>({});
  const [showExitPrompt, setShowExitPrompt] = useState(false);
  const [isForceClosing, setIsForceClosing] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const isPending = isAdding || isRemoving || isReceiving;

  // Persist maintenance_request_id on the cart so it can be resumed later.
  // When switching to a different maintenance request, clear stale cart items
  // first so assets from the previous request don't leak into the new one.
  const patchedRef = useRef<string | null>(null);
  useEffect(() => {
    const cartId = cartData?.cart?.id;
    if (!cartId || !maintenanceRequestId) return;
    if (cartData.cart?.maintenance_request_id === maintenanceRequestId) return;
    if (patchedRef.current === `${cartId}:${maintenanceRequestId}`) return;
    patchedRef.current = `${cartId}:${maintenanceRequestId}`;

    const previousRequestId = cartData.cart?.maintenance_request_id;
    if (previousRequestId && userId) {
      // Cart belonged to a different request — release stale reservations first.
      // Omit cartId so the cart entity itself is preserved for the ID update.
      void clearCart({ userId }).then(() =>
        cartService.updateMaintenanceRequestId(cartId, maintenanceRequestId),
      );
    } else {
      void cartService.updateMaintenanceRequestId(cartId, maintenanceRequestId);
    }
  }, [cartData?.cart?.id, cartData?.cart?.maintenance_request_id, maintenanceRequestId, userId, clearCart]);

  const reservedAssetIds = useMemo(() => {
    if (!cartData?.assets) return new Set<string>();
    return new Set(cartData.assets.map((a) => a.entity.id));
  }, [cartData?.assets]);

  const groupedReceivableItems = useMemo(
    () => groupMaintenanceReceivableItems(receivableItems),
    [receivableItems],
  );

  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return groupedReceivableItems;
    }

    return groupedReceivableItems.filter((row) => {
      const parts = [row.asset_name, row.lin ?? '', row.nsn ?? ''];
      return multiWordMatch(parts.join(' '), query);
    });
  }, [groupedReceivableItems, searchText]);

  const cartItems = useMemo(() => {
    if (!cartData?.assets) return [];
    return groupMaintenanceCartItems(cartData.assets);
  }, [cartData?.assets]);

  const cartCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems],
  );

  const getAddableQty = useCallback(
    (row: MaintenanceRequestReceivableItemViewRow) => {
      return row.asset_ids.reduce((count, id) => count + (reservedAssetIds.has(id) ? 0 : 1), 0);
    },
    [reservedAssetIds],
  );

  const resetFormState = useCallback(() => {
    setSearchText('');
    setQtyDrafts({});
  }, []);

  const forceClose = useCallback(() => {
    setShowExitPrompt(false);
    setIsForceClosing(true);
  }, []);

  const handleClose = useCallback(() => {
    if (cartCount > 0) {
      setShowExitPrompt(true);
    } else {
      resetFormState();
      onClose();
    }
  }, [cartCount, resetFormState, onClose]);

  const handleSave = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
    resetFormState();
    forceClose();
  }, [queryClient, resetFormState, forceClose]);

  const handleCancelMaintenance = useCallback(async () => {
    if (isCanceling) return;
    setIsCanceling(true);
    try {
      if (userId && cartData?.cart) {
        await clearCart({ userId, cartId: cartData.cart.id });
        void queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
      }
      resetFormState();
      forceClose();
    } catch {
      // Best-effort cleanup
    } finally {
      setIsCanceling(false);
    }
  }, [isCanceling, userId, cartData?.cart, clearCart, queryClient, resetFormState, forceClose]);

  const handleQtyDraftChange = useCallback((rowId: string, rawValue: string, max: number) => {
    const parsed = Number.parseInt(rawValue, 10);
    setQtyDrafts((prev) => ({
      ...prev,
      [rowId]: clampQuantity(Number.isFinite(parsed) ? parsed : 1, max),
    }));
  }, []);

  const handleAddToCart = useCallback(
    async (row: MaintenanceRequestReceivableItemViewRow) => {
      if (!userId) return;

      const availableAssetIds = row.asset_ids.filter((id) => !reservedAssetIds.has(id));
      const draft = qtyDrafts[row.id] ?? Math.max(availableAssetIds.length, 1);
      const quantity = clampQuantity(draft, availableAssetIds.length);
      // NOTE: Takes first N assets arbitrarily. User can't pick which specific items (e.g., by serial).
      // Future enhancement: allow selection by serial number or original batch if needed.
      const assetIdsToAdd = availableAssetIds.slice(0, quantity);

      if (assetIdsToAdd.length === 0) return;

      await addToCart({ userId, assetIds: assetIdsToAdd });
    },
    [userId, qtyDrafts, reservedAssetIds, addToCart],
  );

  const handleRemoveCartItem = useCallback(
    async (assetIds: string[]) => {
      if (!userId) return;
      await removeFromCart({ userId, assetIds });
    },
    [userId, removeFromCart],
  );

  const handleComplete = useCallback(async () => {
    if (cartCount === 0) {
      return;
    }

    const assetIds = cartItems.flatMap((item) => item.assetIds);
    try {
      await receiveFromMaintenance({ assetIds });
      if (userId && cartData?.cart) {
        try {
          await clearCart({ userId, cartId: cartData.cart.id });
        } catch {
          // Best-effort cleanup
        }
      }
      void queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
      resetFormState();
      onClose();
    } catch {
      // Error notification handled by mutation's onError
    }
  }, [cartItems, cartCount, receiveFromMaintenance, userId, cartData?.cart, clearCart, onClose]);

  const plannerToolbar = (
    <TextField
      value={searchText}
      onChange={(event) => setSearchText(event.target.value)}
      placeholder="Search assets, LIN, NSN..."
      size="small"
      fullWidth
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );

  const columns: GridColDef<MaintenanceRequestReceivableItemViewRow>[] = [
    { field: 'asset_name', headerName: 'Asset Name', flex: 1, minWidth: 240 },
    { field: 'lin', headerName: 'LIN', width: 120 },
    { field: 'nsn', headerName: 'NSN', width: 150 },
    {
      field: 'stock_quantity',
      headerName: 'Qty in Maintenance',
      width: 170,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'qty',
      headerName: 'Qty',
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const remainingQty = getAddableQty(row);
        const draft = qtyDrafts[row.id];
        const value =
          remainingQty <= 0 ? 0 : draft != null ? clampQuantity(draft, remainingQty) : remainingQty;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <TextField
              size="small"
              type="number"
              value={value}
              disabled={remainingQty <= 0}
              slotProps={{ htmlInput: { min: 1, max: remainingQty } }}
              onChange={(event) => handleQtyDraftChange(row.id, event.target.value, remainingQty)}
              sx={{ width: 84 }}
            />
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const remainingQty = getAddableQty(row);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<Add fontSize="small" />}
              disabled={remainingQty <= 0 || isPending}
              onClick={() => void handleAddToCart(row)}
            >
              Add
            </Button>
          </Box>
        );
      },
    },
  ];

  const plannerContent = isError ? (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Failed to load maintenance inventory.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => void refetch()}>
            Retry
          </Button>
        </Box>
      </Stack>
    </Paper>
  ) : (
    <Box sx={{ height: '100%', width: '100%' }}>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        loading={isLoading}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        disableRowSelectionOnClick
        rowHeight={60}
      />
    </Box>
  );

  const sidebarContent = (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Return Cart
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, maxHeight: '40vh', overflowY: 'auto' }}>
          <Stack spacing={1.5}>
            {cartItems.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 2 }}
              >
                No items added yet
              </Typography>
            ) : (
              cartItems.map((item) => (
                <WorkflowCartRow
                  key={item.key}
                  title={item.name}
                  subtitle={[
                    item.sizeLabel ? `Size ${item.sizeLabel}` : null,
                    item.lin !== '—' ? `LIN ${item.lin}` : null,
                    item.nsn !== '—' ? `NSN ${item.nsn}` : null,
                    `Qty ${item.quantity}`,
                  ]
                    .filter(Boolean)
                    .join(' • ')}
                  actions={
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label={`Remove ${item.name} from return cart`}
                      onClick={() => void handleRemoveCartItem(item.assetIds)}
                      disabled={isPending}
                    >
                      <Close fontSize="inherit" />
                    </IconButton>
                  }
                />
              ))
            )}
          </Stack>
        </Paper>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Total Items
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {cartCount} item{cartCount === 1 ? '' : 's'} selected
        </Typography>
      </Box>
    </Stack>
  );

  const footer = (
    <>
      <Button variant="outlined" onClick={handleClose} disabled={isPending}>
        Cancel
      </Button>
      <ViewerGuardedButton
        variant="contained"
        onClick={() => void handleComplete()}
        disabled={isPending || cartCount === 0}
      >
        {isReceiving ? 'Receiving…' : 'Complete'}
      </ViewerGuardedButton>
    </>
  );

  const closeButton = (
    <Button
      variant="outlined"
      startIcon={<WestIcon />}
      onClick={handleClose}
      sx={{ whiteSpace: 'nowrap' }}
    >
      Return to Maintenance
    </Button>
  );

  return (
    <>
      <WorkflowModalLayout
        open={open}
        onClose={handleClose}
        title={
          documentNumber ? `Receive from Maintenance • ${documentNumber}` : 'Receive from Maintenance'
        }
        plannerTitle={
          documentNumber ? `Assets in Maintenance • ${documentNumber}` : 'Assets in Maintenance'
        }
        plannerToolbar={plannerToolbar}
        plannerContent={plannerContent}
        sidebarTitle="Receive Summary"
        sidebarContent={sidebarContent}
        footer={footer}
        closeButton={closeButton}
      />

      <Dialog
        open={showExitPrompt}
        onClose={() => setShowExitPrompt(false)}
        TransitionProps={{
          onExited: () => {
            if (isForceClosing) {
              onClose();
              setIsForceClosing(false);
            }
          },
        }}
        aria-labelledby="exit-confirmation-title"
        aria-describedby="exit-confirmation-description"
      >
        <DialogTitle id="exit-confirmation-title">Items in Cart</DialogTitle>
        <DialogContent>
          <DialogContentText id="exit-confirmation-description">
            You have items in your maintenance receive cart. You can save your progress for later or
            cancel to remove the reserved items.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => void handleCancelMaintenance()}
            color="error"
            disabled={isCanceling}
          >
            Cancel Receive
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={() => setShowExitPrompt(false)}
            color="inherit"
            disabled={isCanceling}
          >
            Back to Cart
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            autoFocus
            disabled={isCanceling}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
